"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { DateRangeCalendar } from "./calendar";
import { getAvailability, getQuote } from "@/app/actions/booking";
import { usd } from "@/lib/money";
import { nightsBetween } from "@/lib/dates";
import { useLang } from "./lang";

type Listing = {
  slug: string;
  nightlyPrice: number;
  maxGuests: number;
};

export function BookingPanel({ listing }: { listing: Listing }) {
  const { t } = useLang();
  const router = useRouter();
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [checkOut, setCheckOut] = useState<string | null>(null);
  const [guests, setGuests] = useState(1);
  const [blocked, setBlocked] = useState<{ checkIn: string; checkOut: string }[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [deposit, setDeposit] = useState<number | null>(null);
  const [nights, setNights] = useState(0);

  useEffect(() => {
    getAvailability(listing.slug).then((r) => {
      if (r.ok) setBlocked(r.ranges);
    });
  }, [listing.slug]);

  useEffect(() => {
    if (!checkIn || !checkOut) {
      setTotal(null);
      setDeposit(null);
      setNights(0);
      return;
    }
    setNights(nightsBetween(checkIn, checkOut));
    getQuote({ slug: listing.slug, checkIn, checkOut, guests }).then((r) => {
      if (r.ok) {
        setTotal(r.quote.totalAmount);
        setDeposit(r.quote.depositAmount);
      }
    });
  }, [checkIn, checkOut, guests, listing.slug]);

  return (
    <section className="px-4 mt-6 space-y-4">
      <DateRangeCalendar
        checkIn={checkIn}
        checkOut={checkOut}
        blocked={blocked}
        onChange={(from, to) => {
          setCheckIn(from);
          setCheckOut(to);
        }}
      />
      <div className="card p-4 flex items-center justify-between">
        <label className="text-sm text-muted">{t("guestsLabel")}</label>
        <select
          className="field w-28 py-2"
          value={guests}
          onChange={(e) => setGuests(Number(e.target.value))}
        >
          {Array.from({ length: listing.maxGuests }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {t.guests(n)}
            </option>
          ))}
        </select>
      </div>
      <div className="card p-4">
        <div className="flex justify-between text-sm">
          <span className="text-muted">
            {usd(listing.nightlyPrice)} × {t.nights(nights || 0)}
          </span>
          <span>{total ? usd(total) : "—"}</span>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <span className="text-muted">{t("depositToday")}</span>
          <span className="font-semibold">{deposit ? usd(deposit) : "—"}</span>
        </div>
        <button
          className="btn btn-primary w-full mt-4"
          disabled={!checkIn || !checkOut}
          onClick={() => {
            if (!checkIn || !checkOut) return;
            const q = new URLSearchParams({ checkIn, checkOut, guests: String(guests) });
            router.push(`/book/${listing.slug}?${q.toString()}`);
          }}
        >
          {t("reserve")}
        </button>
      </div>
    </section>
  );
}
